import { useState } from 'react';
import { X, Plus, GripVertical, ChevronDown, ChevronUp, Link, MousePointerClick, Type, Image, Video, Music, AlertCircle, Shuffle, Info, Copy, Check, Square, RectangleHorizontal, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import type { ContentBlock, BlockButton, UrlEntry, ImageAspectRatio } from '@/types/flow';
import type { PageVariable } from '@/pages/Meta/Connection/types/meta.types';
import MediaLibraryModal from '@/pages/Meta/Flow/Builder/components/MediaLibraryModal';
import SmartTextField from './SmartTextField';
import { aspectClass, detectAspectFromUrl } from '@/lib/image-aspect';
import UtmPreview from '@/pages/Meta/Flow/Builder/components/config/shared/UtmPreview';
import TemplatePickerDialog from '@/pages/Meta/TemplateLibrary/components/TemplatePickerDialog';
import { templateToBlocks } from '@/pages/Meta/TemplateLibrary/lib/templateToBlocks';
import type { TemplateLibraryItem } from '@/types/template-library';

type BlockType = 'text' | 'image' | 'video' | 'audio';

const blockTypes = [
    { type: 'text' as BlockType, icon: Type, label: 'Texto', color: 'bg-blue-500' },
    { type: 'image' as BlockType, icon: Image, label: 'Foto', color: 'bg-indigo-500' },
    { type: 'video' as BlockType, icon: Video, label: 'Vídeo', color: 'bg-red-500' },
    { type: 'audio' as BlockType, icon: Music, label: 'Áudio', color: 'bg-cyan-500' },
];

interface StepConfigureBlocksProps {
    blocks: ContentBlock[];
    onChange: (blocks: ContentBlock[]) => void;
    pageId?: string;
    pageVariables?: PageVariable[];
    /** ID do broadcast em edição. Quando setado, o <UtmPreview> mostra a URL final
     *  com `utm_source=facebroad_{page}&utm_content=broadcast-{id}&utm_term=broadcast`.
     *  Quando undefined (criação de broadcast novo), mostra placeholder `{broadcast_id}`. */
    broadcastId?: number;
}

export default function StepConfigureBlocks({ blocks, onChange, pageId, pageVariables = [], broadcastId }: StepConfigureBlocksProps) {
    const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);
    const [mediaModalIndex, setMediaModalIndex] = useState<number | null>(null);
    const [mediaModalType, setMediaModalType] = useState<'image' | 'video' | 'audio'>('image');
    const [pickerOpen, setPickerOpen] = useState(false);
    const { toast } = useToast();

    const handleApplyTemplate = (template: TemplateLibraryItem) => {
        const newBlocks = templateToBlocks(template);
        const hadContent = blocks.length > 0;
        onChange(newBlocks);
        toast({
            title: 'Modelo aplicado',
            description: hadContent
                ? `Blocos anteriores foram substituídos. Você pode editar antes de salvar.`
                : `${newBlocks.length} bloco${newBlocks.length > 1 ? 's' : ''} adicionado${newBlocks.length > 1 ? 's' : ''}.`,
        });
    };

    const addBlock = (type: BlockType) => {
        const newBlock: ContentBlock = { type };
        if (type === 'text') newBlock.message = '';
        onChange([...blocks, newBlock]);
        setIsAddMenuOpen(false);
    };

    const removeBlock = (index: number) => {
        onChange(blocks.filter((_, i) => i !== index));
    };

    const updateBlock = (index: number, updates: Partial<ContentBlock>) => {
        onChange(blocks.map((block, i) => (i === index ? { ...block, ...updates } : block)));
    };

    const moveBlock = (index: number, direction: 'up' | 'down') => {
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        if (newIndex < 0 || newIndex >= blocks.length) return;
        const newBlocks = [...blocks];
        [newBlocks[index], newBlocks[newIndex]] = [newBlocks[newIndex], newBlocks[index]];
        onChange(newBlocks);
    };

    const addButton = (blockIndex: number, type: 'url' | 'postback') => {
        const block = blocks[blockIndex];
        const current = block.buttons || [];
        if (current.length >= 3) return;
        const newButton: BlockButton = {
            text: '',
            type,
            value: '',
            url_entries: type === 'url' ? [{ url: '', weight: 100 }] : undefined,
        };
        updateBlock(blockIndex, { buttons: [...current, newButton] });
    };

    const updateButton = (blockIndex: number, btnIndex: number, updates: Partial<BlockButton>) => {
        const block = blocks[blockIndex];
        const newButtons = (block.buttons || []).map((btn, i) =>
            i === btnIndex ? { ...btn, ...updates } : btn
        );
        updateBlock(blockIndex, { buttons: newButtons });
    };

    const removeButton = (blockIndex: number, btnIndex: number) => {
        const block = blocks[blockIndex];
        const newButtons = (block.buttons || []).filter((_, i) => i !== btnIndex);
        updateBlock(blockIndex, { buttons: newButtons });
    };

    // ── URL entries helpers ──────────────────────────────────────────────────

    const addUrlEntry = (blockIndex: number, btnIndex: number) => {
        const btn = blocks[blockIndex].buttons?.[btnIndex];
        if (!btn) return;
        const current = btn.url_entries || [];
        const nextCount = current.length + 1;
        const equalWeight = Math.floor(100 / nextCount);

        // Redistribuir pesos para somar 100%
        const newEntries: UrlEntry[] = current.map((e) => ({
            ...e,
            weight: equalWeight,
        }));
        newEntries.push({
            url: '',
            weight: 100 - equalWeight * (nextCount - 1),
        });

        updateButton(blockIndex, btnIndex, { url_entries: newEntries });
    };

    const updateUrlEntry = (
        blockIndex: number,
        btnIndex: number,
        entryIndex: number,
        updates: Partial<UrlEntry>
    ) => {
        const btn = blocks[blockIndex].buttons?.[btnIndex];
        if (!btn) return;
        const newEntries = (btn.url_entries || []).map((e, i) =>
            i === entryIndex ? { ...e, ...updates } : e
        );
        updateButton(blockIndex, btnIndex, { url_entries: newEntries });
    };

    const removeUrlEntry = (blockIndex: number, btnIndex: number, entryIndex: number) => {
        const btn = blocks[blockIndex].buttons?.[btnIndex];
        if (!btn) return;
        const newEntries = (btn.url_entries || []).filter((_, i) => i !== entryIndex);
        updateButton(blockIndex, btnIndex, { url_entries: newEntries });
    };

    const getTotalWeight = (entries: UrlEntry[]) =>
        entries.reduce((sum, e) => sum + (Number(e.weight) || 0), 0);

    // ── Block URL entries helpers ───────────────────────────────────────────

    const addBlockUrlEntry = (blockIndex: number) => {
        const block = blocks[blockIndex];
        let entries: UrlEntry[] = [];
        try {
            const raw = block.default_action_url || '';
            if (raw.startsWith('[') || raw.startsWith('{')) {
                entries = JSON.parse(raw);
                if (!Array.isArray(entries)) entries = [];
            } else if (raw) {
                entries = [{ url: raw, weight: 100 }];
            }
        } catch {
            entries = [];
        }

        const nextCount = entries.length + 1;
        const equalWeight = Math.floor(100 / nextCount);

        // Redistribuir pesos para somar 100%
        const newEntries: UrlEntry[] = entries.map((e) => ({
            ...e,
            weight: equalWeight,
        }));
        newEntries.push({
            url: '',
            weight: 100 - equalWeight * (nextCount - 1),
        });

        updateBlock(blockIndex, { default_action_url: JSON.stringify(newEntries) });
    };

    const updateBlockUrlEntry = (
        blockIndex: number,
        entryIndex: number,
        updates: Partial<UrlEntry>
    ) => {
        const block = blocks[blockIndex];
        let entries: UrlEntry[] = [];
        try {
            const raw = block.default_action_url || '';
            entries = JSON.parse(raw);
        } catch {
            entries = [];
        }

        const newEntries = entries.map((e, i) =>
            i === entryIndex ? { ...e, ...updates } : e
        );
        updateBlock(blockIndex, { default_action_url: JSON.stringify(newEntries) });
    };

    const removeBlockUrlEntry = (blockIndex: number, entryIndex: number) => {
        const block = blocks[blockIndex];
        let entries: UrlEntry[] = [];
        try {
            const raw = block.default_action_url || '';
            entries = JSON.parse(raw);
        } catch {
            entries = [];
        }

        const newEntries = entries.filter((_, i) => i !== entryIndex);
        // Se sobrar apenas 1, podemos manter como JSON ou converter pra string simples. 
        // Vamos manter como JSON se for explicitamente editado como weighted.
        updateBlock(blockIndex, { default_action_url: JSON.stringify(newEntries) });
    };

    const handleMediaSelect = async (media: any) => {
        if (mediaModalIndex === null) return;
        const idx = mediaModalIndex;
        const updates: any = {
            media_url: media.file_url,
            media_filename: media.filename,
        };
        setMediaModalIndex(null);
        updateBlock(idx, updates);
        // Auto-detect do aspect — só pra imagens, e só se ainda não foi setado
        // explicitamente pelo user.
        if (media.file_url && blocks[idx]?.type === 'image' && !blocks[idx]?.image_aspect_ratio) {
            try {
                const aspect = await detectAspectFromUrl(media.file_url);
                updateBlock(idx, { image_aspect_ratio: aspect });
            } catch { /* mantém default no envio */ }
        }
    };

    const getBlockTypeInfo = (type: string) => blockTypes.find((bt) => bt.type === type);

    return (
        <div className="space-y-6">
            <div className="text-center mb-4">
                <h3 className="text-xl font-semibold text-gray-900">Monte sua Mensagem</h3>
                <p className="text-sm text-gray-600 mt-2">
                    Adicione blocos para compor o conteúdo do broadcast
                </p>
                <div className="mt-3 flex justify-center">
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setPickerOpen(true)}
                        className="border-violet-200 text-violet-700 hover:bg-violet-50 hover:text-violet-800"
                    >
                        <BookOpen className="w-3.5 h-3.5 mr-1.5" />
                        Usar modelo da biblioteca
                    </Button>
                </div>
            </div>

            <BroadcastLeadVariablesHint />

            <TemplatePickerDialog
                open={pickerOpen}
                onOpenChange={setPickerOpen}
                onSelect={handleApplyTemplate}
            />

            {/* Blocks list */}
            {blocks.length > 0 && (
                <div className="space-y-3">
                    {blocks.map((block, index) => {
                        const info = getBlockTypeInfo(block.type);
                        const Icon = info?.icon;
                        const blockButtons = block.buttons || [];

                        return (
                            <div
                                key={index}
                                className="border border-gray-200 rounded-xl bg-white overflow-hidden shadow-sm"
                            >
                                {/* Block header */}
                                <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-100">
                                    <GripVertical className="w-4 h-4 text-gray-300" />
                                    <div className={`${info?.color || 'bg-gray-500'} p-1.5 rounded-md`}>
                                        {Icon && <Icon className="w-3.5 h-3.5 text-white" />}
                                    </div>
                                    <span className="text-sm font-medium text-gray-700 flex-1">
                                        {info?.label || block.type}
                                    </span>
                                    <div className="flex items-center gap-1">
                                        <button
                                            onClick={() => moveBlock(index, 'up')}
                                            disabled={index === 0}
                                            className="p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-30 rounded hover:bg-gray-100"
                                        >
                                            <ChevronUp className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => moveBlock(index, 'down')}
                                            disabled={index === blocks.length - 1}
                                            className="p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-30 rounded hover:bg-gray-100"
                                        >
                                            <ChevronDown className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => removeBlock(index)}
                                            className="p-1.5 text-gray-400 hover:text-red-500 rounded hover:bg-red-50"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>

                                {/* Block content */}
                                <div className="p-4">
                                    {block.type === 'text' && (
                                        <SmartTextField
                                            value={block.message || ''}
                                            onChange={msg => updateBlock(index, { message: msg })}
                                            variables={pageVariables}
                                            placeholder="Digite o texto da mensagem..."
                                            rows={3}
                                        />
                                    )}

                                    {(block.type === 'image' || block.type === 'video' || block.type === 'audio') && (
                                        <div className="space-y-3">
                                            {block.media_url ? (
                                                <div className="space-y-3">
                                                    <div className="flex items-start gap-3">
                                                        {block.type === 'image' && (
                                                            <div className={`w-32 ${aspectClass(block.image_aspect_ratio)} rounded-lg border overflow-hidden bg-gray-50 shrink-0`}>
                                                                <img
                                                                    src={block.media_url}
                                                                    alt="Preview"
                                                                    className="w-full h-full object-cover"
                                                                />
                                                            </div>
                                                        )}
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm text-gray-600 truncate">
                                                                {block.media_filename || 'Arquivo selecionado'}
                                                            </p>
                                                            <button
                                                                onClick={() => {
                                                                    setMediaModalIndex(index);
                                                                    setMediaModalType(block.type as 'image' | 'video' | 'audio');
                                                                }}
                                                                className="text-sm text-[#7dd3fc] hover:text-[#0f172a] mt-1 font-medium"
                                                            >
                                                                Trocar arquivo
                                                            </button>
                                                        </div>
                                                    </div>

                                                    {/* Formato (só pra imagem em template) */}
                                                    {block.type === 'image' && (
                                                        <div className="space-y-1.5">
                                                            <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                Formato da imagem
                                                            </label>
                                                            <div className="grid grid-cols-2 gap-2">
                                                                {(['horizontal', 'square'] as ImageAspectRatio[]).map((opt) => {
                                                                    const active = (block.image_aspect_ratio ?? 'horizontal') === opt;
                                                                    const Icon = opt === 'horizontal' ? RectangleHorizontal : Square;
                                                                    return (
                                                                        <button
                                                                            key={opt}
                                                                            type="button"
                                                                            onClick={() => updateBlock(index, { image_aspect_ratio: opt })}
                                                                            className={`flex items-center gap-2 p-2 rounded-lg border-2 text-sm transition-all ${
                                                                                active
                                                                                    ? 'border-[#7dd3fc] bg-[#7dd3fc]/10'
                                                                                    : 'border-gray-200 hover:border-gray-300'
                                                                            }`}
                                                                        >
                                                                            <Icon className="h-4 w-4 text-gray-500" />
                                                                            <span className="text-xs">
                                                                                {opt === 'horizontal' ? 'Horizontal' : 'Quadrado'}
                                                                                <span className="text-gray-400 ml-1">
                                                                                    {opt === 'horizontal' ? '1.91:1' : '1:1'}
                                                                                </span>
                                                                            </span>
                                                                        </button>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={() => {
                                                        setMediaModalIndex(index);
                                                        setMediaModalType(block.type as 'image' | 'video' | 'audio');
                                                    }}
                                                    className="w-full py-6 border-2 border-dashed border-gray-300 rounded-xl hover:border-[#7dd3fc] hover:bg-[#7dd3fc]/5 transition-colors flex flex-col items-center gap-2"
                                                >
                                                    {Icon && <Icon className="w-8 h-8 text-gray-400" />}
                                                    <span className="text-sm text-gray-500">
                                                        Selecionar {info?.label?.toLowerCase()}
                                                    </span>
                                                </button>
                                            )}
                                            {block.type === 'image' && (
                                                <div className="space-y-2">
                                                    <Input
                                                        placeholder="Título (opcional)"
                                                        value={block.title || ''}
                                                        onChange={(e) => updateBlock(index, { title: e.target.value })}
                                                        className="text-sm"
                                                        maxLength={80}
                                                    />
                                                    <Input
                                                        placeholder="Subtítulo (opcional)"
                                                        value={block.subtitle || ''}
                                                        onChange={(e) => updateBlock(index, { subtitle: e.target.value })}
                                                        className="text-sm"
                                                        maxLength={80}
                                                    />
                                                    {/* ── Dynamic Click URL UI ── */}
                                                    <div className="space-y-2 pt-2 border-t border-gray-100">
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex items-center gap-1.5">
                                                                <Link className="w-3.5 h-3.5 text-gray-400" />
                                                                <span className="text-xs font-semibold text-gray-600">URL ao clicar na imagem</span>
                                                            </div>
                                                            {(() => {
                                                                let blockEntries: UrlEntry[] = [];
                                                                try {
                                                                    const raw = block.default_action_url || '';
                                                                    if (raw.startsWith('[') || raw.startsWith('{')) {
                                                                        blockEntries = JSON.parse(raw);
                                                                    } else if (raw) {
                                                                        blockEntries = [{ url: raw, weight: 100 }];
                                                                    }
                                                                } catch { blockEntries = []; }

                                                                if (blockEntries.length === 0) return null;
                                                                const tw = getTotalWeight(blockEntries);
                                                                const ok = tw === 100;
                                                                return (
                                                                    <span className={`text-[10px] font-mono font-semibold px-2 py-0.5 rounded-full ${ok ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                                                                        {tw}%
                                                                    </span>
                                                                );
                                                            })()}
                                                        </div>

                                                        {(() => {
                                                            let blockEntries: UrlEntry[] = [];
                                                            try {
                                                                const raw = block.default_action_url || '';
                                                                if (raw.startsWith('[') || raw.startsWith('{')) {
                                                                    blockEntries = JSON.parse(raw);
                                                                } else if (raw) {
                                                                    blockEntries = [{ url: raw, weight: 100 }];
                                                                }
                                                            } catch { blockEntries = []; }

                                                            if (blockEntries.length === 0) {
                                                                return (
                                                                    <button
                                                                        onClick={() => addBlockUrlEntry(index)}
                                                                        className="w-full py-2 text-xs border border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 text-gray-500 hover:text-blue-600 flex items-center justify-center gap-1.5"
                                                                    >
                                                                        <Plus className="w-3.5 h-3.5" />
                                                                        Configurar URL de clique
                                                                    </button>
                                                                );
                                                            }

                                                            return (
                                                                <div className="space-y-2">
                                                                    {blockEntries.map((entry, entryIdx) => (
                                                                        <div key={entryIdx} className="bg-gray-50 border border-gray-200 rounded-lg p-2.5 space-y-1.5">
                                                                            <div className="flex items-center justify-between mb-1">
                                                                                <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">URL {entryIdx + 1}</span>
                                                                                <button
                                                                                    onClick={() => removeBlockUrlEntry(index, entryIdx)}
                                                                                    className="p-0.5 text-gray-400 hover:text-red-500"
                                                                                >
                                                                                    <X className="w-3 h-3" />
                                                                                </button>
                                                                            </div>
                                                                            <SmartTextField
                                                                                value={entry.url}
                                                                                onChange={url => updateBlockUrlEntry(index, entryIdx, { url })}
                                                                                variables={pageVariables}
                                                                                placeholder="{{DOMAIN}}{{URL1}}?utm_source={{UTM_SOURCE}}"
                                                                                singleLine
                                                                                inputClassName="h-7 text-xs"
                                                                            />
                                                                            <div className="flex items-center gap-2">
                                                                                <span className="text-xs text-gray-500 whitespace-nowrap">Peso (%)</span>
                                                                                <Input
                                                                                    type="number"
                                                                                    min={0}
                                                                                    max={100}
                                                                                    placeholder="0"
                                                                                    value={entry.weight === 0 && entry.url === '' ? '' : entry.weight}
                                                                                    onChange={(e) => updateBlockUrlEntry(index, entryIdx, { weight: Number(e.target.value) || 0 })}
                                                                                    className="h-7 text-xs w-20 bg-white"
                                                                                />
                                                                            </div>
                                                                            <UtmPreview
                                                                                url={entry.url}
                                                                                mode="broadcast"
                                                                                broadcastId={broadcastId}
                                                                                compact={entryIdx > 0}
                                                                            />
                                                                        </div>
                                                                    ))}

                                                                    <button
                                                                        onClick={() => addBlockUrlEntry(index)}
                                                                        className="w-full py-1.5 text-xs border border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 text-gray-500 hover:text-blue-700 flex items-center justify-center gap-1"
                                                                    >
                                                                        <Plus className="w-3.5 h-3.5" />
                                                                        Adicionar URL
                                                                    </button>
                                                                </div>
                                                            );
                                                        })()}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Block buttons */}
                                {blockButtons.length > 0 && (
                                    <div className="px-4 pb-3 space-y-3">
                                        {blockButtons.map((btn, btnIdx) => {
                                            const isUrl = btn.type === 'url';
                                            const entries = btn.url_entries || [];
                                            const totalWeight = getTotalWeight(entries);
                                            const weightOk = Math.round(totalWeight) === 100;

                                            return (
                                                <div
                                                    key={btnIdx}
                                                    className="border border-gray-200 rounded-xl overflow-hidden"
                                                >
                                                    {/* Button header */}
                                                    <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 border-b border-gray-100">
                                                        {isUrl ? (
                                                            <Link className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                                        ) : (
                                                            <MousePointerClick className="w-4 h-4 text-green-500 flex-shrink-0" />
                                                        )}
                                                        <span className="text-xs font-semibold text-gray-600 flex-1">
                                                            Botão {btnIdx + 1} — {isUrl ? 'URL' : 'Ação'}
                                                        </span>
                                                        <button
                                                            onClick={() => removeButton(index, btnIdx)}
                                                            className="p-1 text-gray-400 hover:text-red-500 rounded hover:bg-red-50"
                                                        >
                                                            <X className="w-3.5 h-3.5" />
                                                        </button>
                                                    </div>

                                                    <div className="p-3 space-y-3">
                                                        {/* Button text */}
                                                        <div>
                                                            <label className="block text-xs text-gray-500 mb-1">Texto do botão</label>
                                                            <Input
                                                                placeholder="Ex: 🍷 READ TEXT"
                                                                value={btn.text}
                                                                onChange={(e) => updateButton(index, btnIdx, { text: e.target.value })}
                                                                className="h-8 text-sm"
                                                                maxLength={20}
                                                            />
                                                        </div>

                                                        {isUrl ? (
                                                            /* ── URL entries ── */
                                                            <div className="space-y-2">
                                                                <div className="flex items-center justify-between">
                                                                    <div className="flex items-center gap-1.5">
                                                                        <Shuffle className="w-3.5 h-3.5 text-blue-500" />
                                                                        <span className="text-xs font-semibold text-gray-600">URLs com peso (random)</span>
                                                                    </div>
                                                                    {entries.length > 0 && (
                                                                        <span className={`text-xs font-mono font-semibold px-2 py-0.5 rounded-full ${weightOk ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                                                                            {totalWeight}%
                                                                        </span>
                                                                    )}
                                                                </div>

                                                                {entries.map((entry, entryIdx) => (
                                                                    <div key={entryIdx} className="bg-blue-50 border border-blue-100 rounded-lg p-2.5 space-y-1.5">
                                                                        <div className="flex items-center justify-between mb-1.5">
                                                                            <span className="text-xs font-semibold text-blue-700">URL {entryIdx + 1}</span>
                                                                            {entries.length > 1 && (
                                                                                <button
                                                                                    onClick={() => removeUrlEntry(index, btnIdx, entryIdx)}
                                                                                    className="p-0.5 text-blue-400 hover:text-red-500"
                                                                                >
                                                                                    <X className="w-3 h-3" />
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                        <SmartTextField
                                                                            value={entry.url}
                                                                            onChange={url => updateUrlEntry(index, btnIdx, entryIdx, { url })}
                                                                            variables={pageVariables}
                                                                            placeholder="{{DOMAIN}}{{URL1}}?utm_source={{UTM_SOURCE}}"
                                                                            singleLine
                                                                            inputClassName="h-7 text-xs"
                                                                        />
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="text-xs text-blue-600 whitespace-nowrap">Peso (%)</span>
                                                                            <Input
                                                                                type="number"
                                                                                min={0}
                                                                                max={100}
                                                                                placeholder="0"
                                                                                value={entry.weight === 0 && entry.url === '' ? '' : entry.weight}
                                                                                onChange={(e) => updateUrlEntry(index, btnIdx, entryIdx, { weight: Number(e.target.value) || 0 })}
                                                                                className="h-7 text-xs w-20 bg-white"
                                                                            />
                                                                        </div>
                                                                        <UtmPreview
                                                                            url={entry.url}
                                                                            mode="broadcast"
                                                                            broadcastId={broadcastId}
                                                                            compact={entryIdx > 0}
                                                                        />
                                                                    </div>
                                                                ))}

                                                                {/* Weight warning */}
                                                                {entries.length > 0 && !weightOk && (
                                                                    <div className="flex items-center gap-1.5 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-2.5 py-1.5">
                                                                        <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                                                                        <span>A soma dos pesos deve ser 100%. Atual: <strong>{totalWeight}%</strong></span>
                                                                    </div>
                                                                )}

                                                                <button
                                                                    onClick={() => addUrlEntry(index, btnIdx)}
                                                                    className="w-full py-1.5 text-xs border border-dashed border-blue-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 text-blue-500 hover:text-blue-700 flex items-center justify-center gap-1"
                                                                >
                                                                    <Plus className="w-3.5 h-3.5" />
                                                                    Adicionar URL
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            /* ── Postback ── */
                                                            <div>
                                                                <label className="block text-xs text-gray-500 mb-1">Payload</label>
                                                                <Input
                                                                    placeholder="payload"
                                                                    value={btn.value}
                                                                    onChange={(e) => updateButton(index, btnIdx, { value: e.target.value })}
                                                                    className="h-8 text-sm"
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}


                                {/* Add button to block */}
                                {blockButtons.length < 3 && (
                                    <div className="px-4 pb-3 flex gap-2">
                                        <button
                                            onClick={() => addButton(index, 'url')}
                                            className="flex-1 py-2 text-sm border border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 text-gray-500 hover:text-blue-600 flex items-center justify-center gap-1.5"
                                        >
                                            <Link className="w-3.5 h-3.5" />
                                            Botão URL
                                        </button>
                                        <button
                                            onClick={() => addButton(index, 'postback')}
                                            className="flex-1 py-2 text-sm border border-dashed border-gray-300 rounded-lg hover:border-green-400 hover:bg-green-50 text-gray-500 hover:text-green-600 flex items-center justify-center gap-1.5"
                                        >
                                            <MousePointerClick className="w-3.5 h-3.5" />
                                            Botão Ação
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Add block */}
            {isAddMenuOpen ? (
                <div className="grid grid-cols-2 gap-3">
                    {blockTypes.map((bt) => {
                        const Icon = bt.icon;
                        return (
                            <button
                                key={bt.type}
                                onClick={() => addBlock(bt.type)}
                                className="flex items-center gap-3 p-4 rounded-xl border-2 border-gray-200 hover:border-[#7dd3fc] hover:bg-[#7dd3fc]/5 transition-all"
                            >
                                <div className={`${bt.color} p-2 rounded-lg`}>
                                    <Icon className="w-5 h-5 text-white" />
                                </div>
                                <span className="text-sm font-medium text-gray-700">{bt.label}</span>
                            </button>
                        );
                    })}
                    <button
                        onClick={() => setIsAddMenuOpen(false)}
                        className="col-span-2 text-sm text-gray-400 hover:text-gray-600 py-2"
                    >
                        Cancelar
                    </button>
                </div>
            ) : (
                <button
                    onClick={() => setIsAddMenuOpen(true)}
                    className="w-full py-4 border-2 border-dashed border-gray-300 rounded-xl hover:border-[#7dd3fc] hover:bg-[#7dd3fc]/5 transition-colors flex items-center justify-center gap-2 text-gray-500 hover:text-[#0f172a]"
                >
                    <Plus className="w-5 h-5" />
                    <span className="text-sm font-medium">Adicionar Bloco</span>
                </button>
            )}

            {/* Info */}
            {blocks.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-900">
                        <strong>Dica:</strong> Cada bloco será enviado como uma mensagem separada no Messenger, na ordem em que aparecem acima. Nos botões URL, você pode adicionar várias URLs com pesos — o sistema sorteia aleatoriamente na hora do envio.
                    </p>
                </div>
            )}

            {/* Media Library Modal */}
            {pageId && (
                <MediaLibraryModal
                    isOpen={mediaModalIndex !== null}
                    onClose={() => setMediaModalIndex(null)}
                    onSelect={handleMediaSelect}
                    pageId={pageId}
                    mediaType={mediaModalType}
                />
            )}
        </div>
    );
}

// ────────────────────────────────────────────────────────────────────────
//  BroadcastLeadVariablesHint — variáveis disponíveis nas mensagens.
//  Substituição é feita no backend em services/broadcast_sender.py.
// ────────────────────────────────────────────────────────────────────────
function BroadcastLeadVariablesHint() {
    const [copied, setCopied] = useState<string | null>(null);
    const variables = [
        { token: '#first_name#', desc: 'Primeiro nome do lead', sample: 'João' },
        { token: '#LEAD_FIRST_NAME#', desc: 'Primeiro nome (alias canônico)', sample: 'João' },
        { token: '#LEAD_USER_FIRST_NAME#', desc: 'Primeiro nome (alias completo)', sample: 'João' },
    ];

    const copyToClipboard = (token: string) => {
        navigator.clipboard.writeText(token).catch(() => { /* fallback ignorado */ });
        setCopied(token);
        setTimeout(() => setCopied(null), 1500);
    };

    return (
        <div className="rounded-lg border border-blue-100 bg-blue-50/50 px-3 py-2.5">
            <div className="flex items-center gap-1.5 mb-2">
                <Info className="w-3.5 h-3.5 text-blue-600" />
                <span className="text-xs font-semibold text-blue-900">
                    Variáveis disponíveis
                </span>
                <span className="text-[10px] text-blue-700/70">
                    (clique pra copiar — substituídas automaticamente no envio)
                </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
                {variables.map((v) => (
                    <button
                        type="button"
                        key={v.token}
                        onClick={() => copyToClipboard(v.token)}
                        title={`${v.desc} — Ex.: "${v.sample}"`}
                        className="group flex items-center gap-1 px-2 py-1 rounded-md bg-white border border-blue-200 hover:border-blue-400 hover:bg-blue-50 transition-colors"
                    >
                        <code className="text-[11px] font-mono text-blue-700 font-medium">
                            {v.token}
                        </code>
                        {copied === v.token ? (
                            <Check className="w-3 h-3 text-green-600" />
                        ) : (
                            <Copy className="w-3 h-3 text-blue-400 group-hover:text-blue-600" />
                        )}
                    </button>
                ))}
            </div>
            <p className="text-[10px] text-blue-700/70 mt-2 leading-relaxed">
                Variantes também aceitas: <code className="font-mono">{'{{$first_name}}'}</code>,{' '}
                <code className="font-mono">{'{{$last_name}}'}</code>,{' '}
                <code className="font-mono">{'{{$full_name}}'}</code> — equivalente às de cima pra last/full.
            </p>
        </div>
    );
}
